import React, { useContext, useId, useEffect, useState, useRef, useCallback } from "react";
import { CommonContext } from "hooks/CommonContext";
import OpenCandidateResume from "components/OpenCandidateResume";
import PropTypes from "prop-types";
import CameraJoinPageBox from "./CameraJoinPageBox";
import AllowMicroCameraPermissionBox from "components/ui-components/AllowMicroCameraPermissionBox";
import useIsMobile from "hooks/useIsMobile";
import { FaVideo, FaVideoSlash, FaMicrophone, FaMicrophoneSlash, FaChevronUp } from "react-icons/fa";
import { MdCheck } from "react-icons/md";

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

  .ijp-page {
    min-height: calc(100vh - 56px);
    display: flex;
    align-items: center;
    justify-content: center;
    background: #080d18;
    padding: 40px 32px;
    font-family: 'Inter', system-ui, sans-serif;
    box-sizing: border-box;
    background-image:
      radial-gradient(ellipse 55% 50% at 15% 60%, rgba(14,165,233,0.06) 0%, transparent 65%),
      radial-gradient(ellipse 45% 45% at 85% 35%, rgba(99,102,241,0.07) 0%, transparent 65%);
  }

  .ijp-card {
    display: flex;
    align-items: center;
    gap: 64px;
    width: 100%;
    max-width: 980px;
    flex-wrap: wrap;
    justify-content: center;
  }

  .ijp-cam-col {
    flex: 0 0 auto;
    width: clamp(280px, 46vw, 520px);
    min-width: 0;
    filter: drop-shadow(0 8px 40px rgba(0,0,0,0.55));
  }

  .ijp-cam-inner {
    border-radius: 18px;
    overflow: hidden;
    background: #0f1629;
    border: 1px solid #1e2d4a;
    width: 100%;
  }

  /* ── Device selector row ── */
  .ijp-devices {
    margin-top: 10px;
    display: flex;
    gap: 8px;
  }

  /* Each device group — toggle button + separator + chevron */
  .ijp-dev-group {
    position: relative;
    display: flex;
    align-items: center;
    background: #0f1629;
    border: 1px solid #1e2d4a;
    border-radius: 10px;
    flex: 1;
    min-width: 0;
    transition: border-color 0.15s;
  }

  .ijp-dev-group:focus-within,
  .ijp-dev-group:hover { border-color: #263554; }

  /* Toggle — icon + truncated device name */
  .ijp-dev-toggle {
    all: unset;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 9px 10px;
    cursor: pointer;
    flex: 1;
    min-width: 0;
    font-size: 12.5px;
    font-weight: 500;
    color: #c4d0ea;
    transition: color 0.14s;
  }

  .ijp-dev-toggle:hover { color: #f0f4ff; }
  .ijp-dev-toggle.ijp-dev-off { color: #fca5a5; }
  .ijp-dev-toggle.ijp-dev-off:hover { color: #fecaca; }

  .ijp-dev-toggle-icon { font-size: 14px; flex-shrink: 0; }

  .ijp-dev-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
  }

  /* Separator */
  .ijp-dev-sep {
    width: 1px;
    height: 20px;
    background: #1e2d4a;
    flex-shrink: 0;
  }

  /* Chevron trigger */
  .ijp-dev-chevron {
    all: unset;
    box-sizing: border-box;
    width: 32px;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #4a5f82;
    font-size: 10px;
    flex-shrink: 0;
    border-radius: 0 9px 9px 0;
    transition: color 0.14s, background 0.14s, transform 0.2s;
  }

  .ijp-dev-chevron:hover { color: #c4d0ea; background: rgba(255,255,255,0.04); }
  .ijp-dev-chevron.ijp-dev-open { color: #22d3ee; transform: rotate(180deg); }

  /* Dropdown panel */
  .ijp-dev-dropdown {
    position: absolute;
    bottom: calc(100% + 8px);
    left: 0;
    right: 0;
    background: #0f1629;
    border: 1px solid #1e2d4a;
    border-radius: 12px;
    padding: 6px;
    z-index: 300;
    box-shadow: 0 16px 48px rgba(0,0,0,0.6);
    min-width: 220px;
    animation: ijpDdIn 0.12s ease;
  }

  @keyframes ijpDdIn {
    from { opacity: 0; transform: translateY(4px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .ijp-dev-dd-label {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #4a5f82;
    padding: 6px 10px 4px;
  }

  .ijp-dev-option {
    all: unset;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 8px 10px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 13px;
    color: #8b9dc3;
    transition: background 0.12s, color 0.12s;
  }

  .ijp-dev-option:hover { background: rgba(34,211,238,0.07); color: #e8eeff; }
  .ijp-dev-option.ijp-dev-sel { color: #22d3ee; font-weight: 600; }

  .ijp-dev-check {
    font-size: 15px;
    color: #22d3ee;
    flex-shrink: 0;
    visibility: hidden;
  }

  .ijp-dev-sel .ijp-dev-check { visibility: visible; }

  .ijp-dev-opt-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ijp-dev-none {
    padding: 8px 10px;
    font-size: 12.5px;
    color: #4a5f82;
  }

  /* ── Info panel ── */
  .ijp-info { flex: 1 1 320px; min-width: 0; }

  .ijp-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: rgba(34, 211, 238, 0.1);
    border: 1px solid rgba(34, 211, 238, 0.28);
    color: #22d3ee;
    border-radius: 20px;
    padding: 5px 14px 5px 10px;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin-bottom: 22px;
  }

  .ijp-badge-dot {
    width: 7px; height: 7px;
    border-radius: 50%;
    background: #22d3ee;
    flex-shrink: 0;
    animation: ijpDot 2s ease-in-out infinite;
  }

  @keyframes ijpDot {
    0%, 100% { opacity: 1; transform: scale(1); }
    50%       { opacity: 0.4; transform: scale(0.72); }
  }

  .ijp-title {
    font-size: clamp(24px, 3vw, 32px);
    font-weight: 700;
    color: #f0f4ff;
    line-height: 1.2;
    margin: 0 0 26px;
    letter-spacing: -0.025em;
  }

  .ijp-divider { border: none; border-top: 1px solid #1e2d4a; margin: 0 0 22px; }

  .ijp-row {
    display: grid;
    grid-template-columns: 106px 1fr;
    gap: 4px 14px;
    margin-bottom: 16px;
    align-items: start;
  }

  .ijp-label { font-size: 12.5px; font-weight: 500; color: #8b9dc3; padding-top: 2px; }
  .ijp-value { font-size: 13.5px; font-weight: 500; color: #dbe4f8; line-height: 1.55; }

  .ijp-btn {
    margin-top: 28px;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 13px 30px;
    background: linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%);
    color: #04111a;
    border: none;
    border-radius: 50px;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    box-shadow: 0 4px 20px rgba(34, 211, 238, 0.30);
    transition: transform 0.15s ease, box-shadow 0.18s ease, filter 0.18s ease;
    white-space: nowrap;
    letter-spacing: 0.01em;
  }

  .ijp-btn:hover:not(:disabled) {
    filter: brightness(1.08);
    transform: translateY(-1px);
    box-shadow: 0 6px 28px rgba(34, 211, 238, 0.42);
  }

  .ijp-btn:active:not(:disabled) { transform: translateY(0); }

  .ijp-btn:disabled {
    background: #1e2d4a; color: #526080;
    cursor: not-allowed; box-shadow: none; filter: none;
  }

  .ijp-btn-arrow { font-size: 17px; line-height: 1; transition: transform 0.18s; }
  .ijp-btn:hover:not(:disabled) .ijp-btn-arrow { transform: translateX(4px); }


  /* ── Mic level indicator ── */
  .ijp-mic-bar {
    margin-top: 10px;
    width: 100%;
    background: #0f1629;
    border: 1px solid #1e2d4a;
    border-radius: 10px;
    padding: 10px 14px;
    display: flex;
    align-items: center;
    gap: 12px;
    box-sizing: border-box;
    transition: border-color 0.2s;
  }
  .ijp-mic-bar.ijp-mic-active { border-color: rgba(74,222,128,0.35); }
  .ijp-mic-bar-icon { font-size: 15px; color: #4a5f82; flex-shrink: 0; transition: color 0.2s; display: flex; align-items: center; justify-content: center; line-height: 1; }
  .ijp-mic-bar.ijp-mic-active .ijp-mic-bar-icon { color: #4ade80; }
  .ijp-mic-bar-label { font-size: 12px; font-weight: 500; color: #6b7fa8; flex-shrink: 0; width: 36px; }
  .ijp-mic-bar.ijp-mic-active .ijp-mic-bar-label { color: #4ade80; }
  .ijp-mic-track { flex: 1; height: 4px; background: #1e2d4a; border-radius: 4px; overflow: hidden; }
  .ijp-mic-fill { height: 100%; border-radius: 4px; background: linear-gradient(90deg,#22d3ee 0%,#4ade80 100%); transition: width 0.08s ease-out; }
  .ijp-mic-eq { display: flex; align-items: flex-end; gap: 2px; height: 18px; flex-shrink: 0; }
  .ijp-mic-eq-bar { width: 3px; border-radius: 2px; background: #4ade80; transition: height 0.08s ease-out; min-height: 2px; }

  @media (max-width: 680px) {
    /* Allow internal scroll when content is taller than viewport on small phones */
    .ijp-page {
      height: 100%;
      overflow-y: auto;
      padding: 16px 12px 88px;
      align-items: flex-start;
      -webkit-overflow-scrolling: touch;
    }
    .ijp-card {
      flex-direction: column;
      gap: 24px;
      align-items: stretch;
    }
    .ijp-cam-col {
      width: 100%;
      min-width: 0;
      flex: none;
      /* drop-shadow can cause content to clip/overflow on mobile — remove it */
      filter: none;
    }
    .ijp-cam-inner {
      border-radius: 14px;
    }
    .ijp-devices {
      flex-direction: column;
      gap: 6px;
    }
    .ijp-info {
      flex: 1 1 100%;
      min-width: 0;
    }
    .ijp-badge {
      margin-bottom: 14px;
    }
    .ijp-title {
      font-size: 22px;
      margin-bottom: 18px;
    }
    .ijp-divider {
      margin: 0 0 16px;
    }
    .ijp-row {
      grid-template-columns: 88px 1fr;
      margin-bottom: 12px;
    }

    .ijp-btn {
      position: fixed;
      bottom: 16px;
      left: 16px;
      right: 16px;
      width: auto;
      justify-content: center;
      z-index: 20;
      margin-top: 0;
    }
  }

  @media (max-width: 400px) {
    .ijp-page          { padding: 12px 8px; }
    .ijp-badge         { font-size: 9.5px; }
    .ijp-title         { font-size: 19px; }
    .ijp-dev-dropdown  { min-width: 180px; }
  }
`;

let _inj = false;
const inject = () => {
  if (_inj) return;
  const s = document.createElement("style");
  s.textContent = STYLES;
  document.head.appendChild(s);
  _inj = true;
};

/* ── useDeviceSelector ────────────────────────────────────────────────────────
   Enumerates cameras + mics, tracks selected device IDs, and switches the
   active media stream when the user picks a different device.
   Fires a CustomEvent so CameraFeed / peer connections can swap the track.
──────────────────────────────────────────────────────────────────────────── */
const useDeviceSelector = () => {
  const [cameras, setCameras] = useState([]);
  const [mics, setMics] = useState([]);
  const [camId, setCamId] = useState("");
  const [micId, setMicId] = useState("");

  const enumerate = useCallback(async () => {
    try {
      // Request permission first so labels are populated
      await navigator.mediaDevices.getUserMedia({ video: true, audio: true }).catch(() => { });
      const devices = await navigator.mediaDevices.enumerateDevices();
      const vids = devices.filter((d) => d.kind === "videoinput");
      const auds = devices.filter((d) => d.kind === "audioinput");
      setCameras(vids);
      setMics(auds);
      setCamId((prev) => prev || vids[0]?.deviceId || "");
      setMicId((prev) => prev || auds[0]?.deviceId || "");
    } catch (e) {
      console.warn("[DeviceSelector] enumerate failed", e);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    enumerate();
    navigator.mediaDevices.addEventListener("devicechange", enumerate);
    return () => navigator.mediaDevices.removeEventListener("devicechange", enumerate);
  }, [enumerate]);

  const switchCamera = useCallback(async (deviceId) => {
    setCamId(deviceId);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: deviceId } },
      });
      const [track] = stream.getVideoTracks();
      window.dispatchEvent(new CustomEvent("bizro:switchCamera", { detail: { deviceId, track } }));
    } catch (e) {
      console.warn("[DeviceSelector] switchCamera failed", e);
    }
  }, []);

  const switchMic = useCallback(async (deviceId) => {
    setMicId(deviceId);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: deviceId } },
      });
      const [track] = stream.getAudioTracks();
      window.dispatchEvent(new CustomEvent("bizro:switchMic", { detail: { deviceId, track } }));
    } catch (e) {
      console.warn("[DeviceSelector] switchMic failed", e);
    }
  }, []);

  return { cameras, mics, camId, micId, switchCamera, switchMic };
};

/* ── useMicLevel ────────────────────────────────────────────────────────────── */
const useMicLevel = (micId, audioEnabled) => {
  const [level, setLevel] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const rafRef = useRef(null);
  const ctxRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    if (!audioEnabled) { setLevel(0); setIsSpeaking(false); return; }
    let cancelled = false;
    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: micId ? { deviceId: { exact: micId } } : true, video: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.5;
        ctx.createMediaStreamSource(stream).connect(analyser);
        ctxRef.current = ctx;
        const buf = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
          if (cancelled) return;
          analyser.getByteFrequencyData(buf);
          const avg = buf.reduce((s, v) => s + v, 0) / buf.length;
          const pct = Math.min(100, Math.round((avg / 128) * 100));
          setLevel(pct); setIsSpeaking(pct > 8);
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
      } catch (e) { console.warn("[MicLevel] failed", e); }
    };
    start();
    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ctxRef.current) ctxRef.current.close().catch(() => { });
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, [micId, audioEnabled]);

  return { level, isSpeaking };
};

const EQ_BARS = 4;
const MicLevelBar = ({ level, isSpeaking, audioEnabled }) => {
  const barHeights = Array.from({ length: EQ_BARS }, (_, i) => {
    if (!isSpeaking) return 2;
    return Math.max(3, Math.min(18, Math.round(level * 0.18 * [1, 1.4, 0.8, 1.2][i])));
  });
  return (
    <div className={`ijp-mic-bar${isSpeaking ? " ijp-mic-active" : ""}`}>
      <div className="ijp-mic-bar-icon">
        {audioEnabled ? <FaMicrophone /> : <FaMicrophoneSlash />}
      </div>
      <div className="ijp-mic-bar-label">
        {!audioEnabled ? "Muted" : isSpeaking ? "Live" : "Mic"}
      </div>
      <div className="ijp-mic-track">
        <div className="ijp-mic-fill" style={{ width: audioEnabled ? `${level}%` : "0%" }} />
      </div>
      <div className="ijp-mic-eq">
        {barHeights.map((h, i) => (
          <div key={i} className="ijp-mic-eq-bar" style={{ height: `${h}px`, opacity: isSpeaking ? 1 : 0.25 }} />
        ))}
      </div>
    </div>
  );
};

/* ── DeviceGroup ──────────────────────────────────────────────────────────────
   Toggle button (with icon + truncated device name) + chevron + dropdown.
   Matches Zoom's UI pattern: click the icon area to toggle, click the chevron
   to pick a different device.
──────────────────────────────────────────────────────────────────────────── */
const DeviceGroup = ({ icon, offIcon, isOn, onToggle, devices, selectedId, onSelect, label }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const selected = devices.find((d) => d.deviceId === selectedId);
  const displayName = selected?.label || label;

  return (
    <div className="ijp-dev-group" ref={ref}>
      {/* Toggle */}
      <button
        className={`ijp-dev-toggle${!isOn ? " ijp-dev-off" : ""}`}
        onClick={onToggle}
        title={isOn ? `Turn off ${label}` : `Turn on ${label}`}
      >
        <span className="ijp-dev-toggle-icon">{isOn ? icon : offIcon}</span>
        <span className="ijp-dev-label">{displayName}</span>
      </button>

      {/* Divider */}
      <div className="ijp-dev-sep" />

      {/* Chevron */}
      <button
        className={`ijp-dev-chevron${open ? " ijp-dev-open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        title={`Choose ${label}`}
      >
        <FaChevronUp />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="ijp-dev-dropdown">
          <div className="ijp-dev-dd-label">Select {label}</div>
          {devices.length === 0 ? (
            <div className="ijp-dev-none">No devices found</div>
          ) : (
            devices.map((d, i) => (
              <button
                key={d.deviceId}
                className={`ijp-dev-option${d.deviceId === selectedId ? " ijp-dev-sel" : ""}`}
                onClick={() => { onSelect(d.deviceId); setOpen(false); }}
              >
                <MdCheck className="ijp-dev-check" />
                <span className="ijp-dev-opt-name">
                  {d.label || `${label} ${i + 1}`}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

DeviceGroup.propTypes = {
  icon: PropTypes.node, offIcon: PropTypes.node,
  isOn: PropTypes.bool, onToggle: PropTypes.func,
  devices: PropTypes.array, selectedId: PropTypes.string,
  onSelect: PropTypes.func, label: PropTypes.string,
};

/* ── InterviewerJoinPage ─────────────────────────────────────────────────── */
const InterviewerJoinPage = ({ joinRoomHandler }) => {
  inject();
  const { interviewDetails, microphoneError, toggleVideo, toggleAudio, userOwnData, audioEnabled } =
    useContext(CommonContext);
  const idHook = useId();
  const isMobile = useIsMobile(); // eslint-disable-line no-unused-vars
  const { cameras, mics, camId, micId, switchCamera, switchMic } = useDeviceSelector();
  const { level, isSpeaking } = useMicLevel(micId, !!audioEnabled && !microphoneError);

  return (
    <div className="ijp-page">
      <div className="ijp-card">

        {/* Camera preview */}
        <div className="ijp-cam-col">
          <div className="ijp-cam-inner">
            {microphoneError
              ? <AllowMicroCameraPermissionBox />
              : <CameraJoinPageBox />}
          </div>

          {/* Device selectors — Zoom-style, below camera card */}
          {!microphoneError && (
            <>
              <div className="ijp-devices">
                <DeviceGroup
                  icon={<FaVideo />}
                  offIcon={<FaVideoSlash />}
                  isOn={!!userOwnData?.isCameraOn}
                  onToggle={toggleVideo}
                  devices={cameras}
                  selectedId={camId}
                  onSelect={switchCamera}
                  label="Camera"
                />
                <DeviceGroup
                  icon={<FaMicrophone />}
                  offIcon={<FaMicrophoneSlash />}
                  isOn={!!audioEnabled}
                  onToggle={toggleAudio}
                  devices={mics}
                  selectedId={micId}
                  onSelect={switchMic}
                  label="Microphone"
                />
              </div>
              <MicLevelBar level={level} isSpeaking={isSpeaking} audioEnabled={!!audioEnabled} />
            </>
          )}
        </div>

        {/* Interview details */}
        <div className="ijp-info">
          <div className="ijp-badge">
            <span className="ijp-badge-dot" />
            Upcoming Interview
          </div>

          <h1 className="ijp-title">
            {interviewDetails?.jobDetails?.displayName} Interview
          </h1>

          <hr className="ijp-divider" />

          <div className="ijp-row">
            <span className="ijp-label">Candidate</span>
            <span className="ijp-value">
              {interviewDetails?.candidateDetails?.firstName}{" "}
              {interviewDetails?.candidateDetails?.lastName}
            </span>
          </div>

          <div className="ijp-row">
            <span className="ijp-label">Resume</span>
            <span className="ijp-value"><OpenCandidateResume /></span>
          </div>

          <div className="ijp-row">
            <span className="ijp-label">Interviewers</span>
            <span className="ijp-value">
              {interviewDetails?.interviewerName?.map((iv, i) => (
                <span key={"iv-" + idHook + iv.firstName + iv.lastName}>
                  {iv.firstName} {iv.lastName}
                  {interviewDetails.interviewerName.length > i + 1 && ", "}
                </span>
              ))}
            </span>
          </div>

          <hr className="ijp-divider" />

          <button
            disabled={!!microphoneError}
            className="ijp-btn"
            onClick={() => joinRoomHandler()}
          >
            Join interview
            <span className="ijp-btn-arrow">→</span>
          </button>
        </div>
      </div>
    </div>
  );
};

InterviewerJoinPage.propTypes = { joinRoomHandler: PropTypes.any };
export default InterviewerJoinPage;